import _ from "lodash";
import React from "react";
import { Button, Col, Form, Modal } from "react-bootstrap";
import { connect } from "react-redux";
import { defaultAuths, getAppName, NOT_FOR_CLINICAL_USE_LABELING } from "../../../environments";
import { getAreConcurrentUploadsEnabled, setAreConcurrentUploadsEnabled } from "../../../local-storage";

import * as sagas from '../../../store/sagas';
import { StoreState } from "../../../store/store";
import { User } from "../../../store/user";
import AppAuth from "../../../web-apis/app-auth";
import { Backend } from "../../../web-apis/backends";
import { Checkbox } from "../../misc-components";
import ModalDialog from "../ModalDialog";

import './UserSettingsDialog.css';

type OwnProps = {}

type DispatchProps = {
    setUserSettingsDialogVisibility(value: boolean): void,
    setUserSettingBackend(backend: Backend | null): void,
    logIntoAppAuth: (appAuth: AppAuth) => void,
    requestLogOut: () => void,
    setUserSettingPatientInfoVisibility(showPatientInfo: boolean): void,
}

type OwnState = {
    /** This state prop is only used to get the settings react component to self-update. 
     * The actual value is read from and written to localStorage. */
    areConcurrentUploadsEnabledHack: boolean,
}

type AllProps = OwnProps & StoreState & DispatchProps;

class UserSettingsDialog extends React.Component<AllProps, OwnState> {

    constructor(props: AllProps) {
        super(props);

        this.state = {
            areConcurrentUploadsEnabledHack: getAreConcurrentUploadsEnabled(),
        };
    }

    isDefaultAppAuth = (appAuth: AppAuth) => {
        return defaultAuths.includes(appAuth.appName);
    }

    handleClose = () => {
        this.props.setUserSettingsDialogVisibility(false);
    }

    handleLogOutClick = () => {
        this.props.requestLogOut();
    }

    handleLogInClick = (appAuth: AppAuth) => {
        this.props.logIntoAppAuth(appAuth);
    }

    handleUploadConcurrencyChange = () => {
        const newValue = !getAreConcurrentUploadsEnabled();
        setAreConcurrentUploadsEnabled(newValue);
        this.setState({ areConcurrentUploadsEnabledHack: newValue });
    }

    handleShowPatientInfoChange = (newValue: boolean) => {
        this.props.setUserSettingPatientInfoVisibility(newValue);
    }

    render() {
        const isVisible = this.props.isUserSettingsDialogVisible;

        const { labeling } = this.props;

        const user = this.props.user as User;

        const areConcurrentUploadsEnabled = getAreConcurrentUploadsEnabled();

        return (
            <ModalDialog
                show={isVisible}
                onHide={this.handleClose}
                size="lg">

                <Modal.Header closeButton>
                    <Modal.Title>User Session Settings</Modal.Title>
                </Modal.Header>
                <Modal.Body className="user-settings">
                    <div className="notice">Note that these settings are only active in this session/tab.</div>
                    <Form>
                        <Form.Group>
                            <Form.Row>
                                <Col lg={6}>
                                    <div title="Display DICOM metadata such as Patient ID, Series Description, etc on main view">Display DICOM metadata information on main view</div>
                                </Col>
                                <Col className="value-row-shift-checkbox">
                                    <Checkbox
                                        isSelected={user.showPatientInfo}
                                        onCheckboxChange={() => this.handleShowPatientInfoChange(!user.showPatientInfo)}
                                    />
                                </Col>
                            </Form.Row>
                        </Form.Group>

                        <hr />

                        <Form.Group>
                            <h3>Local settings</h3>

                            <div className="notice">
                                These settings apply to every session/tab of {getAppName()} in this browser.
                                <br />
                                Note that these settings won't be applied until after you refresh this tab.
                            </div>

                            <Form.Row>
                                <Col lg={6}>
                                    <div title="Disabling this option will limit file uploads to one at a time.">Enable concurrent uploads</div>
                                </Col>
                                <Col className="value-row-shift-checkbox">
                                    <Checkbox
                                        label=""
                                        isSelected={areConcurrentUploadsEnabled}
                                        onCheckboxChange={this.handleUploadConcurrencyChange}
                                    />
                                </Col>
                            </Form.Row>

                        </Form.Group>

                        {labeling !== undefined && (
                            <>
                                <hr />

                                <Form.Group>
                                    <h3>Labeling</h3>

                                    <div className="notice"><span className="local-page-not-for-clinical">{NOT_FOR_CLINICAL_USE_LABELING}.</span></div>

                                    <Form.Row>
                                        <Col lg={6}><b>System version</b></Col><Col>{labeling.systemVersion}</Col>
                                        <Col lg={6}><b>Manufacturer</b></Col><Col>{labeling.manufacturer}</Col>
                                        <Col lg={6}><b>Manufacturer model name</b></Col><Col>{labeling.manufacturerModelName}</Col>
                                        <Col lg={6}><b>Description</b></Col><Col>{labeling.description}</Col>
                                        <Col lg={6}><b>Clinical</b></Col><Col>{labeling.clinical ? 'For clinical use.' : 'For non-clinical use.'}</Col>
                                        <Col lg={6}><b>UDI</b></Col><Col>{labeling.udi}</Col>
                                    </Form.Row>

                                </Form.Group>
                            </>
                        )}

                        <hr />

                        <Form.Group>
                            <Form.Row className="logout-button">
                                <Col lg={6}>
                                    <div>Logged in as <b><span title={user.email}>{user.username}</span></b></div>
                                </Col>
                                <Col className="value-row-shift-checkbox">
                                    <Button variant="danger" onClick={() => this.handleLogOutClick()}>Log out of {getAppName()}</Button>
                                </Col>
                            </Form.Row>
                        </Form.Group>

                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={this.handleClose}>Ok</Button>
                </Modal.Footer>
            </ModalDialog>
        );
    }
}

export default connect(
    state => Object.assign({}, state),
    sagas.mapDispatchToProps,
)(UserSettingsDialog);
